import { FC, memo, useContext, useMemo } from 'react';
import { Field, useFormikContext } from 'formik';
import Box from '@mui/material/Box';

import AmountInput from '../../../../components/AmountInput/AmountInput';
import AutocompleteByOptionId from '../../../../components/AutocompleteByOptionId';
import { renderCurrencyOption } from '../../../../components/CurrencyMenuItem/CurrencyMenuItem';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Client } from '../../../../api/clients/types';
import { Currency } from '../../../../api/currencies/types';
import { taytanFormatNumberToString } from '../../../../utils/mathUtils';
import RateControlBox from './RateControlBox';

type Props = {
    /** Source doc — forwarded to RateControlBox for source label + default date */
    documentGenerationData?: any;
    /** Amount field name in Formik. Defaults to 'amount'. */
    amountFieldName?: string;
    /** Override amount display value (useful when form tracks amount in separate state, e.g. Receipt's amountLeftToPay). */
    amountValue?: number;
    /** Called after Formik's `amount` is updated. Use for side-effects (e.g. netToRemit). */
    onAmountChange?: (raw: number) => void;
    /** Currency field name. Defaults to 'currency'. */
    currencyFieldName?: string;
    /** Hide the currency picker (e.g. PRQ uses primary currency only). */
    hideCurrency?: boolean;
    /** Hide the amount input (e.g. Receipt with CHECK payment method shows checks array instead). */
    hideAmount?: boolean;
    /** Disable the currency picker. */
    disableCurrency?: boolean;
    /** Restrict the currency picker to this list instead of the full CurrenciesContext
     *  (e.g. Credit Card is limited to ILS/USD — see CreditCardPaymentPlanFields). */
    currencyOptionsOverride?: Currency[];
    /** Amount label. Defaults to 'Amount'. Currency code + local-equivalent auto-appended. */
    amountLabel?: string;
    /** Extra className on AmountInput (e.g. rateAffectBase highlight). */
    amountClassName?: string;
    /** Disable all inputs. */
    disabled?: boolean;
    /** Grid xs for the Amount column. Defaults to 7. */
    amountXs?: number;
    /** Grid xs for the Currency column. Defaults to 5. */
    currencyXs?: number;
    /** External date override for rate (e.g., payment value date). Forwarded to RateControlBox. */
    rateDateOverride?: string | Date | null;
    /** When true, force the rate date baseline to today and ignore override/source defaults. */
    forceTodayDefault?: boolean;
    /** Disable the rate controls (rate input, date picker, Change Rate button). */
    disableRateControl?: boolean;
    /** Disable only the Rate Date picker. Forwarded to RateControlBox. */
    disableRateDate?: boolean;
    /** Tooltip explaining why the Rate Date picker is disabled. Forwarded to RateControlBox. */
    rateDateDisabledTooltip?: string;
};

const AmountCurrencyRateBox: FC<Props> = ({
    documentGenerationData,
    amountFieldName = 'amount',
    amountValue,
    onAmountChange,
    currencyFieldName = 'currency',
    hideCurrency = false,
    hideAmount = false,
    disableCurrency = false,
    currencyOptionsOverride,
    amountLabel = 'Amount',
    amountClassName,
    disabled = false,
    amountXs = 7,
    currencyXs = 5,
    rateDateOverride,
    forceTodayDefault = false,
    disableRateControl = false,
    disableRateDate = false,
    rateDateDisabledTooltip
}) => {
    const { setFieldValue, values } = useFormikContext<any>();
    const currencies = useContext(CurrenciesContext);
    const clientsContext = useContext(ClientsContext);
    const localCurrency: string = (clientsContext.selected as Client)?.currencySettings?.localCurrency as string;

    const currencyOptions = useMemo(
        () => currencyOptionsOverride ?? currencies.dataAsArray,
        [currencyOptionsOverride, currencies.dataAsArray]
    );

    const currencyRaw = values[currencyFieldName];
    const currId: string = typeof currencyRaw === 'object' ? currencyRaw?._id : currencyRaw;

    const localCurrencyCode: string = useMemo(() => {
        const cur = currencyOptions.find((c: Currency) => c._id === localCurrency);
        return cur?.code || cur?.symbol || '';
    }, [currencyOptions, localCurrency]);

    const displayAmount = amountValue !== undefined ? amountValue : values[amountFieldName];

    // Build label: "Amount — 308.00 ILS" when in non-local currency (currency shown in the Currency dropdown).
    const fullAmountLabel: string = useMemo(() => {
        if (!values.rate || !displayAmount || !localCurrencyCode) return amountLabel;
        if (currId === localCurrency) return amountLabel;
        const local = taytanFormatNumberToString(Math.round(displayAmount * values.rate * 100) / 100);
        return `${amountLabel} — ${local} ${localCurrencyCode}`;
    }, [amountLabel, values.rate, displayAmount, currId, localCurrency, localCurrencyCode]);

    const amountFlex = hideCurrency ? 12 : amountXs;
    const currencyFlex = hideAmount ? 12 : currencyXs;

    return (
        <>
            {(!hideAmount || !hideCurrency) && (
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    {!hideAmount && (
                        <Box sx={{ flex: amountFlex, minWidth: 0 }}>
                            <AmountInput
                                name={amountFieldName}
                                value={displayAmount}
                                label={fullAmountLabel}
                                className={amountClassName}
                                onChange={(raw) => {
                                    setFieldValue(amountFieldName, raw);
                                    onAmountChange?.(raw);
                                }}
                            />
                        </Box>
                    )}
                    {!hideCurrency && (
                        <Box sx={{ flex: currencyFlex, minWidth: 0 }}>
                            <Field
                                component={AutocompleteByOptionId}
                                name={currencyFieldName}
                                label="Currency"
                                options={currencyOptions}
                                renderOption={renderCurrencyOption}
                                onChange={setFieldValue}
                                nameKey="code"
                                fullOption
                                disableClearable
                                disabled={disabled || disableCurrency}
                                required
                            />
                        </Box>
                    )}
                </Box>
            )}
            <RateControlBox
                currencyId={currId}
                documentGenerationData={documentGenerationData}
                rateDateOverride={rateDateOverride}
                forceTodayDefault={forceTodayDefault}
                disabled={disableRateControl}
                disableRateDate={disableRateDate}
                rateDateDisabledTooltip={rateDateDisabledTooltip}
            />
        </>
    );
};

export default memo(AmountCurrencyRateBox);
